import React, { useRef, useState, useEffect } from "react";
import {
  Search,
  Minus,
  Plus,
  X,
  ChevronLeft,
  ChevronRight,
  Trash2
} from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faImage,faChevronDown, faPlusCircle, faQuestionCircle, faTrash, faX , faExclamationTriangle, faInfoCircle, faMapMarkerAlt, faSearch  } from "@fortawesome/free-solid-svg-icons";
import { decryptData } from "../../../utils/storageCrypto";
import useCommonResponseHandler from '../../CommenResponseAndErrorHandlers/CommenResponseHandler';
import { Loader ,Spinner } from '../../Loader/Loader';
import PlacesAutocomplete, { geocodeByAddress, getLatLng } from 'react-places-autocomplete';
import { Msg200or201, Msg204, Msg400,Msg409 } from '../../Messages/Messages';

// Custom hook to handle outside clicks
const useOutsideClick = (ref, callback) => {
  const handleClick = (e) => {
    if (ref.current && !ref.current.contains(e.target)) {
      callback();
    }
  };

  React.useEffect(() => {
    document.addEventListener('click', handleClick);
    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, []);
};

const CustomDropdown = ({ options, selected, onSelect, placeholder = "Select", showSearch = true, showAddNew = false, addNewText = "Add New Address" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [focusedOptionIndex, setFocusedOptionIndex] = useState(null);
  const dropdownRef = React.useRef(null);
  const searchInputRef = useRef(null);

  const filteredOptions = options.filter(option => {
    if (!option || !option.label) return false;
    return option.label.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const handleOptionClick = (option) => {
    onSelect(option);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleKeyDown = (e) => {
    if (!isOpen) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedOptionIndex(prevIndex => {
          const newIndex = prevIndex === null ? 0 : Math.min(prevIndex + 1, filteredOptions.length - 1);
          scrollToOption(newIndex);
          return newIndex;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedOptionIndex(prevIndex => {
          const newIndex = prevIndex === null ? filteredOptions.length - 1 : Math.max(prevIndex - 1, 0);
          scrollToOption(newIndex);
          return newIndex;
        });
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedOptionIndex !== null) {
          handleOptionClick(filteredOptions[focusedOptionIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
    }
  };

  const scrollToOption = (index) => {
    const optionElements = dropdownRef.current.querySelectorAll('div[data-index]');
    if (optionElements && optionElements[index]) {
      optionElements[index].scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  };

  useOutsideClick(dropdownRef, () => setIsOpen(false));
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        className="flex justify-between items-center p-2 border bg-white border-gray-300 rounded-md cursor-pointer hover:border-blue-500 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-200 transition-all"
        onClick={() => setIsOpen(!isOpen)}
        tabIndex={0}
        role="button"
        onKeyDown={handleKeyDown}
      >
        <span className={`text-sm truncate ${selected?.label ? 'text-black font-medium' : 'text-[#838195]'}`}>
          {selected?.label || placeholder}
        </span>
        <FontAwesomeIcon 
          icon={faChevronDown} 
          className={`w-3 h-3 text-gray-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
        />
      </div>
      {isOpen && (
        <div className="absolute w-full max-h-40 bg-white border border-gray-300 rounded-md shadow-xl z-20 flex flex-col overflow-hidden">
          {/* Search Section - Fixed height */}
          {showSearch && (
            <div className="p-2 border-b border-gray-100 bg-gray-50 shrink-0">
              <input
                ref={searchInputRef}
                type="text"
                className="w-full p-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all"
                placeholder=" Search addresses..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </div>
          )}
          
          {/* Options List - Scrollable, reduced height */}
          <div className="overflow-y-auto max-h-30 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent flex-1">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <div
                  key={option.value}
                  data-index={index}
                  className={`px-4 py-2.5 cursor-pointer text-sm border-b border-gray-50 last:border-b-0 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 focus:bg-blue-100 transition-all duration-200 ${
                    index === focusedOptionIndex ? 'bg-blue-100 ring-1 ring-blue-200' : ''
                  }`}
                  onClick={() => handleOptionClick(option)}
                  role="option"
                >
                  <div className="flex items-center space-x-3">
                    {option.icon && <FontAwesomeIcon icon={option.icon} className="text-gray-500 w-4 h-4 flex-shrink-0" />}
                    <span className="font-medium text-gray-800 truncate">{option.label}</span>
                    {option.addressType && (
                      <span className="ml-auto text-xs bg-gray-100 px-2 py-0.5 rounded-full text-gray-600">
                        {option.addressType}
                      </span>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="p-6 text-center">
                <FontAwesomeIcon icon={faSearch} className="mx-auto mb-2 text-gray-400 w-6 h-6" />
                <p className="text-sm text-gray-500">No addresses found</p>
              </div>
            )}
          </div>
          
          {/* Add New Button - Fixed height, always visible */}
         {showAddNew && (
  <div className="border-t border-gray-200 shrink-0">
    <button
      className="w-full flex items-center justify-start pl-4 pr-3 py-2.5 text-sm font-semibold text-blue-600 hover:text-blue-700 hover:bg-blue-100 transition-all duration-200 group"
      onClick={() => handleOptionClick({ value: 'add-new', label: addNewText })}
    >
      <FontAwesomeIcon icon={faPlusCircle} className="mr-2 w-4 h-4 group-hover:scale-110 transition-transform flex-shrink-0" />
      <span>{addNewText}</span>
    </button>
  </div>
)}
        </div>
      )}
    </div>
  );
};


const CreateRFQ = ({ isOpen, onClose }) => {
  const [pricingModel, setPricingModel] = useState("general");
  const [searchQuery, setSearchQuery] = useState("");
  const [items, setItems] = useState([]);
  const [currentImage, setCurrentImage] = useState(0);
  const [allProducts, setAllProducts] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [zoom, setZoom] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState();
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0); // Track retry attempts
  const [deliveryAddresses, setDeliveryAddresses] = useState([]);
  const [selectedDeliveryAddress, setSelectedDeliveryAddress] = useState(null);
  const fetchurl = process.env.REACT_APP_FORE_BRICKBUCKET;
  const imageurl = process.env.REACT_APP_FORE_BRICKBUCKET_IMAGES;
const [showAddAddressModal, setShowAddAddressModal] = useState(false);
const [formData, setFormData] = useState({ projectName: '', areaLocalitySector: '', nearbyLandmark: '', addressType: 'project' });
const [activeTab, setActiveTab] = useState('project');  const FetchProjectsUrl = process.env.REACT_APP_FORE_APILINK; //measurit
  const RETRY_LIMIT = 3; // Maximum number of retries
 const { 
    showPopup, popupMessage, popupType,retryFunction, handleGetResponse, handleSaveResponse, setShowPopup,
    isSaving, setRetryFunction, handleDeleteResponse,  setIsSaving ,    setPopupMessage,
    setPopupType } = useCommonResponseHandler();


  useEffect(() => {
    if (isOpen) {
      fetchAllApprovedCustomers();
      fetchAllItems();
    }
  }, [isOpen]);

  useEffect(() => {
  if (selectedCustomer?.id) {
          fetchDeliveryAddresses(selectedCustomer.id);
  }
}, [selectedCustomer]);


// Add these to your component state (if not already present)
// Add these states with existing states
const [address, setAddress] = useState('');
const [selectedAddressDetails, setSelectedAddressDetails] = useState('');
const [searchText, setSearchText] = useState('');
const [coordinates, setCoordinates] = useState({ lat: 0, lng: 0 });
const [isMapFullyLoaded, setIsMapFullyLoaded] = useState(false);
const [showDetailsForm, setShowDetailsForm] = useState(false);
useEffect(() => {
  if (coordinates.lat && coordinates.lng) {
    // Small delay to ensure DOM is ready
    setTimeout(() => {
      renderMap();
    }, 100);
  }
}, [coordinates]);


    const renderMap = () => {
  if (!window.google || !coordinates.lat || !coordinates.lng) return;
  const mapElement = document.getElementById('rfq-map');
  if (!mapElement) return;
  mapElement.innerHTML = '';
  const map = new window.google.maps.Map(mapElement, {
    center: coordinates,
    zoom: 15
  });
};



const handleSaveAddress = async () => {
  try {
    const encryptedToken = sessionStorage.getItem('token');
    const token = decryptData(encryptedToken);

    // Backend required fields mapping
    const payload = {
      customer_id: selectedCustomer?.id,           
      address_type: activeTab,                   
      area_sector_locality: formData.areaLocalitySector,  
      latitude: formData.latitude || '',           
      longitude: formData.longitude || '',         
      
      // Project specific (if project tab)
      ...(activeTab === 'project' && {
        project_name: formData.projectName,   
        landmark: formData.nearbyLandmark || '',    
      }),
      
      ...(activeTab === 'other' && {
        landmark: formData.nearbyLandmark || ''
      }),
      
      is_default: true,
      is_active: true
    };

    console.log('🗺️ Sending payload:', payload);

    const response = await fetch(`${fetchurl}customer/delivery-address/createDeliveryAddress`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-token': token,
      },
      body: JSON.stringify(payload)
    });

    const data = await response.json();
    console.log('📥 Response:', data);

    if (data.success) {
      // Refresh addresses
      await fetchDeliveryAddresses(selectedCustomer.id);
      setSelectedDeliveryAddress(data.data);
      
      // Reset form
      setFormData({ 
        projectName: '', 
        areaLocalitySector: '', 
        nearbyLandmark: '', 
        addressType: 'project',
        latitude: '',
        longitude: ''
      });
      
      setPopupMessage("Address Saved Successfully!");
  setPopupType('200or201');
  setShowPopup(true);

  setTimeout(() => {
    setShowPopup(false);
    showAddAddressModal(false);
  }, 1000);

    } else {
      console.error('❌ Save failed:', data.message);
      alert(data.message || 'Failed to save address');
    }
  } catch (error) {
    console.error('💥 Error:', error);
    alert('Network error. Please try again.');
  }
};



  const fetchAllItems = async () => {
    try {
          const encryptedToken = sessionStorage.getItem('token');
              const token = decryptData(encryptedToken);
              const encryptedOrganizationId = sessionStorage.getItem('organizationId');
              const organizationId = decryptData(encryptedOrganizationId);

      if (!organizationId) {
        console.error("Organization ID not found in sessionStorage.");
        return;
      }

      const response = await fetch(
        `${fetchurl}product/get-all-products?organization_id=${organizationId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-token": token,
          },
        }
      );

      const data = await response.json();
      console.log("Fetched Products:", data);
      if (data.success && data.data) {
        setAllProducts(data.data);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

const handleQuantityChange = (id, value, index) => {
  setItems((prev) => {
    const newItems = [...prev];
    const item = newItems[index];

    // If quantity is 1 and action is 'dec', remove the item
    if (item.quantity === 1 && value === "dec") {
      return newItems.filter((_, i) => i !== index);
    }

    // Otherwise, update the quantity
    newItems[index] = {
      ...item,
      quantity:
        typeof value === "number"
          ? Math.max(1, value) // Direct input: ensure min 1
          : value === "inc"
          ? item.quantity + 1 // Increment
          : Math.max(1, item.quantity - 1), // Decrement, min 1
    };

    return newItems;
  });
};


  const handleAddItem = () => {
  const trimmedName = searchQuery.trim();
  if (!trimmedName) return;

  const existing = items.find(
    (item) => item.name.toLowerCase() === trimmedName.toLowerCase()
  );

  if (existing) {
    console.log("✅ Item already exists. Increasing quantity:", existing);
    handleQuantityChange(existing.id, "inc");
    setSearchQuery("");
    setShowSuggestions(false);
    return;
  }

  const foundProduct = allProducts.find(
    (p) => p.product_name.toLowerCase() === trimmedName.toLowerCase()
  );

  console.log("🔍 Found Product:", foundProduct);

  let newItem = {
  name: foundProduct ? foundProduct.product_name : trimmedName,
  image: foundProduct?.product_image ? `${imageurl}${foundProduct.product_image}` : null,
  quantity: 1,
  is_available: true,
  product_id:foundProduct._id,
  selling_price:foundProduct.selling_price,
  cost_price:foundProduct.cost_price,
  tax_rate:foundProduct.GST, 
  UOM_id:foundProduct.UOM_id._id
};

 if (foundProduct){
  newItem.product_id = foundProduct._id
  newItem.UOM_id = foundProduct.UOM_id
  newItem.product_category_id = foundProduct.product_category_id._id
 }else{
 newItem.is_available = false
 }

  console.log("🆕 Adding Item:", newItem);
  if (foundProduct?.product_image) {
    console.log("📸 Item has image:", newItem.image);
  } else {
    console.log("⚠️ Item has no image, using placeholder");
  }

  setItems((prev) => [...prev, newItem]);
  setSearchQuery("");
  setShowSuggestions(false);
};


const calculateTotals = (cartItems/* , redeemableItems */) => {

  let subtotal = 0;
  let taxAmount = 0;

  cartItems.forEach(cartItem => {

    // find the redeemable item by ID
    //const redeemItem = redeemableItems?.find(r => r.item_id === cartItem._id);

    // If redeem data not found → no coins
    //const coins = redeemItem ? redeemItem.coins : 0;

    // reduce selling price by coins
    //const effectivePrice = Math.max(cartItem.selling_price - coins, 0);

    // GST % (ex: 18)
    const gstRate = cartItem.GST || cartItem.IGST || 0;
    const eachSubtotal = cartItem.selling_price * cartItem.quantity;
    const effectivePrice = eachSubtotal /* - coins */;
    // Subtotal calculation (normal selling price × qty)
    subtotal += cartItem.selling_price * cartItem.quantity;

    // Correct tax calculation
    taxAmount += (effectivePrice * (gstRate / 100));
  });

  // Round the tax
  taxAmount = Math.round(taxAmount);

  return { subtotal, taxAmount };
};

 const createAndSendQuotation = async () => {
  try {
      console.log('🆕 Creating new quotation');
      // Get cart items from state (only for new quotations)
      if (!items.length) {
        return { success: false, message: 'Add items to create RFQ' };
      }

    console.log("items for rfq:",items);  

    const encryptedToken = sessionStorage.getItem('token');
    const token = decryptData(encryptedToken);
    const enorganizationId = sessionStorage.getItem('organizationId')
    const organizationId = decryptData(enorganizationId);

    const customerId = selectedCustomer?.id;

    if (!customerId || !token || !organizationId) {
      return { success: false, message: 'User information is incomplete or missing' };
    }

let quotationPayload;
const { subtotal, taxAmount } = calculateTotals(items/*,  redeemableItems */);
         
// Calculate total amount including tax and discount
const totalAmount = subtotal + taxAmount;
// Extract unique product category IDs from cart items
 const product_category_ids = [
  ...new Set(items.map(item => item.product_category_id))
];
// Build quotation payload
 quotationPayload = {
  quotation_date: new Date().toISOString(),
  customer_id:customerId ,
  project_id: selectedProject?.id || null,
  delivery_address_id: selectedDeliveryAddress?._id || null,
  tax_preference: "Taxable",
  organizationId: organizationId,
  status: "Pending",
  is_digistore: true,

  // Map cart items into quotation items
  items: items.map(item => {
    const totalPrice = item.selling_price * item.quantity;
    const taxRate = item.tax_rate / 100;
    const totalTaxAmount = totalPrice * taxRate;
    const totalItemAmount = totalPrice + totalTaxAmount;

    

    return {
      item_id: item.product_id,
      item_image: item.image,
      item_name: item.name,
      quantity: item.quantity,
      selling_price: item.selling_price,
      cost_price: item.cost_price,
      tax_rate: Math.round(taxRate),
      total_tax_amount: Math.round(+totalTaxAmount),
      total: Math.round(+totalItemAmount),
      UOM_id: item?.UOM_id,
    };
  }),


  product_category_ids: product_category_ids,
  subtotal: subtotal,
  tax_amount: taxAmount,
  discount: 0,
  total: totalAmount ,
  terms_and_conditions: "Standard terms and conditions apply",
};


    console.log('📤 Sending quotation data:', quotationPayload);

    const endpoint = `${fetchurl}customer/quotations/save-quickbuy-quotation`;
    const method = 'POST';

    // Send to backend
    const response = await fetch(endpoint, {
      method: method,
      headers: {
        'Content-Type': 'application/json',
        'x-token': token,
      },
      body: JSON.stringify(quotationPayload)
    });

    const data = await response.json();
    console.log('📊 Backend response:', data);
   if (response.status === 200 || response.status === 201) {
      // ✅ STEP 1: Show success popup
      setPopupMessage("RFQ Created Successfully!");
      setPopupType('200or201');
      setShowPopup(true);

      // ✅ STEP 2: Auto-close everything after 2 seconds
      setTimeout(() => {
        console.log('🧹 Auto-closing modal and clearing state...');
        setShowPopup(false);        // Close popup first
        onClose();                  // Close modal
        setItems([]);               // Clear items
      }, 1000); // Increased to 2 seco
      return { success: true, quotationNumber: data.data.quotation_number }

    } else {
      return { success: false, message: data.message || 'Failed to process quotation' };
    }

  } catch (error) {
    console.error('Error processing quotation:', error);
    return { success: false, message: 'An error occurred while processing the quotation' };
  }
}; 

    const fetchAllApprovedCustomers = async (attempt = 1) => {
      try {
        const encryptedToken = sessionStorage.getItem('token');
                const token = decryptData(encryptedToken);
                const encryptedOrganizationId = sessionStorage.getItem('organizationId');
                const organizationId = decryptData(encryptedOrganizationId);
        const response = await fetch(fetchurl + `customer/get-all-approved-customers?organizationId=${organizationId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'x-token': token
          }
        });
        const data = await response.json();
        const output = await handleGetResponse(response, data);
       // console.log(output)
        if (output) {
          // Format data for dropdown
          const formattedCustomers = output.map(customer => ({
            id: customer._id,  // Use customer ID as value
            label: customer.customer_name, // Use customer name as label
            value: customer.customer_name
          }));
    
          setCustomers(formattedCustomers); // Update state with formatted data
         // console.log(formattedCustomers);
// Check for customerId in sessionStorage and set selected customer
const scId = sessionStorage.getItem('customerId');
const storedCustomerId = decryptData(scId);
if (storedCustomerId) {
  console.log(storedCustomerId)
  const selectedCustomer = formattedCustomers.find(customer => customer.id === storedCustomerId);
  if (selectedCustomer) {
    console.log(selectedCustomer)
    setSelectedCustomer(selectedCustomer);
    // Optionally fetch related data for this customer
    //fetchAllprojectsbyCustomer(selectedCustomer.id);
  }
}
} else {
if(response.status === 500) {
  setRetryFunction(() => () => fetchAllApprovedCustomers);
}
}
      }  catch (error) {
        console.error('Failed to fetch:', error);
  
        if (attempt < RETRY_LIMIT) {
          setRetryCount(attempt + 1); // Increment retry count
          setTimeout(() => fetchAllApprovedCustomers(attempt + 1), 1000); // Retry after 1 second
        } else {
          setIsLoading(false);
          setPopupMessage('Internal Server Error. Kindly Retry.');
          setPopupType('500');
          setShowPopup(true);
          setRetryFunction(() => () => fetchAllApprovedCustomers(attempt)); // Set retry function
        }
      }finally {
        setIsLoading(false);
      }
    };

    const fetchAllprojectsbyCustomer = async (id, attempt = 1) => {
      //console.log('Fetching projects for customer_id:', id);
      try {
        const encryptedToken = sessionStorage.getItem('token');
        const token = decryptData(encryptedToken);

        const response = await fetch(`${FetchProjectsUrl}/projects/get-projects-by-customer?customer_id=${id}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'x-token': token
          }
        });
        const data = await response.json();
        const output = await handleGetResponse(response, data);
        //console.log(output);
        if (output) {
           const formattedProjects = output.map(project => ({
            id: project?._id,  // Use customer ID as value
            label: project?. projectName, // Use customer name as label
            value: project?.projectName
          }));
    
          setProjects(formattedProjects);

          //console.log(formattedProjects);
        } else {
          if (response.status === 500) {
           // console.log('retry entered');
            setRetryFunction(() => () => fetchAllprojectsbyCustomer(id));
          }
          //console.error(data.message);
        }
      } catch (error) {
        console.error('Failed to fetch projects:', error);
  
        if (attempt < RETRY_LIMIT) {
          setRetryCount(attempt + 1);
          setTimeout(() => fetchAllprojectsbyCustomer(id, attempt + 1), 1000);
        } else {
          setIsLoading(false);
          setPopupMessage('Internal Server Error. Kindly Retry.');
          setPopupType('500');
          setShowPopup(true);
          setRetryFunction(() => () => fetchAllprojectsbyCustomer(id, attempt));
        }
      } finally {
        setIsLoading(false);
      }
    };    

// useEffect(() => {
// if (deliveryAddresses && deliveryAddresses.length > 0 && !selectedDeliveryAddress) {
// const customerId = deliveryAddresses[0].customer_id
// getDefaultDeliveryAddress(customerId)
// }
// }, [deliveryAddresses]);

    const getDefaultDeliveryAddress = async (customer_id) => {
  try {
    if (!customer_id) throw new Error('Customer ID is required');

    const url = process.env.REACT_APP_FORE_BRICKBUCKET;
    const token = sessionStorage.getItem('token');

    const response = await fetch(
      `${url}customer/delivery-address/getDefaultDeliveryAddress?customer_id=${customer_id}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-token': token,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (data.success) {
     setSelectedDeliveryAddress(data.data)
    } else {
      console.error('Failed to fetch default delivery address:', data.message);
      return null;
    }
  } catch (error) {
    console.error('Error fetching default delivery address:', error);
    return null;
  }
};

  const filteredDeliveryAddresses = deliveryAddresses
  .filter(addr => {
    const addrCustomerId =
      typeof addr.customer_id === "object"
        ? addr.customer_id._id
        : addr.customer_id;

    return addrCustomerId === selectedCustomer?.id;
  })
  .map(addr => ({
    value: addr._id,
    addressType: addr.address_type, 
    label: addr.area_sector_locality || "Address not available",
    fullData: addr
  }));

  const handleAddressSelect = async (address) => {
  setAddress(address);
  setFormData(prev => ({ ...prev, areaLocalitySector: address }));
  
  try {
    const results = await geocodeByAddress(address);
    const latLng = await getLatLng(results[0]);
    setFormData(prev => ({
      ...prev,
      latitude: latLng.lat,
      longitude: latLng.lng
    }));
  } catch (error) {
    console.error('Error getting coordinates:', error);
  }
};

const fetchDeliveryAddresses = async (customerId) => {
  try {
    const encryptedToken = sessionStorage.getItem("token");
    const token = decryptData(encryptedToken);

    if (!customerId || !token) return;

    const response = await fetch(
      `${fetchurl}customer/delivery-address/getDeliveryAddresses?customer_id=${customerId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-token": token,
        },
      }
    );

    const data = await response.json();

    console.log("Delivery addresses for selected customer:", data);

    if (data.success) {
      setDeliveryAddresses(data.data || []);
    } else {
      setDeliveryAddresses([]);
    }

  } catch (error) {
    console.error("Error fetching delivery addresses:", error);
    setDeliveryAddresses([]);
  }
};

const closePopup = () => {
  setShowPopup(false);
};

//filtered items
  const filteredProducts =
    allProducts.filter((item) =>
      item.product_name
        ?.toLowerCase()
        .includes(searchQuery.toLowerCase())
    ) || [];

  if (!isOpen) return null;

   const handleCustomerSelect = (option) => {
  setSelectedProject(null); // Reset the selected project
  setSelectedProjectId(null); // Reset the selected project ID
  setSelectedCustomer(option); // Set the selected option object
  
  // ✅ ADD THESE LINES - Clear delivery address when customer changes
  setSelectedDeliveryAddress(null);
  setDeliveryAddresses([]); // Also clear the addresses list
  
  console.log(option);
};

    if (isLoading) {
      return <Loader />;
    }
  
   

  return (
  <div className="fixed inset-0 flex items-top justify-center z-50 p-4">
    <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden shadow-2xl">
      
      {/* Header */}
      <div className="flex justify-between items-center px-5 py-4 border-b border-gray-100">
        <h2 className="text-lg font-semibold text-gray-800">Add Items To Create RFQ</h2>
       <button
  onClick={() => {
    onClose();
    setItems([]);
  }}
  className="p-2 hover:bg-gray-100 rounded-full transition"
>
  <X size={20} className="text-gray-600" />
</button>

      </div>

      {/* Main Content */}

{/* Main Content */}
<div className="flex overflow-hidden flex-1">
  <div className="w-full flex flex-col bg-white px-8 py-5 overflow-hidden">
    
    {/* Scrollable Content Area */}
    <div className="flex-1 overflow-y-auto pb-24"> {/* pb-24 for sticky footer space */}
      
      {/* Pricing Type */}
      <div className="mb-6">
        <p className="text-sm font-semibold text-gray-800 mb-2">Select Pricing Type</p>
        <div className="flex gap-3">
          {["project", "general"].map((type) => (
            <div
              key={type}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl border transition-all duration-200 cursor-pointer ${
                pricingModel === type
                  ? "border-blue-600 bg-blue-50 shadow-sm"
                  : "border-gray-200 hover:border-gray-300"
              }`}
              onClick={() => setPricingModel(type)}
            >
              <div
                className={`w-3 h-3 rounded-full ${
                  pricingModel === type ? "bg-blue-600" : "bg-gray-300"
                }`}
              ></div>
              <span
                className={`text-sm font-medium ${
                  pricingModel === type ? "text-blue-700" : "text-gray-700"
                }`}
              >
                {type === "project" ? "Project Pricing" : "Quick Pricing"}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Customer Selection */}
      <div className="w-full flex flex-row items-center mb-4 mt-5 space-x-2">
        <label htmlFor="customername" className="block text-sm font-regular text-[#e54643] w-1/6">
          Customer Name<span className="text-red-500">*</span>
        </label>
        <div className='w-1/3'>
          <CustomDropdown
            options={customers}
            selected={selectedCustomer}
            onSelect={handleCustomerSelect}
            placeholder='Select Customer'
            showAddNew={false}
            showSearch={false}
          />
        </div>
      </div>

      {/* Delivery Address */}
      <div className="w-full flex flex-row items-center mb-4 mt-5 space-x-2">
        <label htmlFor="deliveryAddress" className="block text-sm font-regular text-[#e54643] w-1/6">
          Delivery Address<span className="text-red-500">*</span>
        </label>
        <div className="w-1/3 space-y-2">
          <CustomDropdown
            options={filteredDeliveryAddresses}
            selected={
              selectedDeliveryAddress
                ? {
                    value: selectedDeliveryAddress._id,
                    label: selectedDeliveryAddress.area_sector_locality || "Address not available",
                    addressType: selectedDeliveryAddress.address_type
                  }
                : null
            }
            onSelect={(option) => {
              if (option.value === 'add-new') {
                setShowAddAddressModal(true);
              } else {
                setSelectedDeliveryAddress(option.fullData);
              }
            }}
            placeholder="Select Delivery Address"
            showSearch={true}
            showAddNew={true}
          />
        </div>
      </div>

      {/* Search + Add Item */}
      <div className="flex items-start gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-3 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Search or add an item..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setShowSuggestions(true);
            }}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400 text-sm transition"
          />
          {showSuggestions && searchQuery && (
            <div className="absolute bg-white border border-gray-200 rounded-xl mt-1 w-full shadow-lg z-10 max-h-48 overflow-y-auto">
              {filteredProducts.length > 0 ? (
                filteredProducts.map((item) => (
                  <div
                    key={item._id}
                    onClick={() => {
                      setSearchQuery(item.product_name);
                      setShowSuggestions(false);
                    }}
                    className="px-4 py-2 hover:bg-blue-50 cursor-pointer text-sm text-gray-700"
                  >
                    {item.product_name}
                  </div>
                ))
              ) : (
                <div className="px-4 py-2 text-sm text-gray-500">No items found</div>
              )}
            </div>
          )}
        </div>
        <button
          onClick={handleAddItem}
          className="text-sm font-medium py-2 px-4 rounded-md border border-[#f7525a] text-[#f7525a] bg-white hover:bg-[#f7525a] hover:text-white transition-colors duration-200 shadow-sm"
        >
          Add Item
        </button>
      </div>

      {/* Added Items List */}
      <div className="space-y-3 mb-4">
        {items.map((item, index) => (
          <div
            key={item.id}
            className="flex items-center justify-between border border-gray-100 rounded-xl p-3 hover:shadow-sm bg-white transition"
          >
            {/* Left: Image + Name */}
            <div className="flex items-center gap-3">
              {item.image ? (
                <img
                  src={item.image}
                  alt={item.name}
                  className="w-10 h-10 rounded-md object-cover"
                />
              ) : (
                <div className="w-10 h-10 rounded-md bg-gray-200 flex items-center justify-center">
                  <FontAwesomeIcon icon={faImage} className="text-gray-400" />
                </div>
              )}
              <span className="font-medium text-gray-800 text-sm">{item.name}</span>
            </div>

            {/* Right: QuantitySelector + Delete Button */}
            <div className="flex items-center gap-2">
              <QuantitySelector
                item={item}
                quantity={item.quantity}
                handleQuantityChange={handleQuantityChange}
                min={1}
                max={100}
                index={index}
              />
              <button
                onClick={() => {
                  setItems((prev) => prev.filter((_, i) => i !== index));
                }}
                className="p-2 hover:bg-red-50 rounded-full transition"
                title="Remove item"
              >
                <Trash2 className="w-4 h-4 text-red-500" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* Sticky Footer Buttons - Always visible at bottom */}
    <div className="sticky bottom-0 left-0 right-0 border-t border-gray-200 pt-4 px-2 pb-2 z-10">
      <div className="flex justify-end gap-3 mx-2">
        <button
          onClick={() => {
            onClose();
            setItems([]);
          }}
          className="px-6 py-2 text-sm font-semibold text-gray-700 bg-white border-2 border-gray-200 rounded-xl hover:bg-gray-50 hover:shadow-md hover:border-gray-300 transition-all duration-200 shadow-sm min-w-[100px]"
        >
          Cancel
        </button>
        <button
          onClick={createAndSendQuotation}
          disabled={!selectedCustomer || !selectedDeliveryAddress || items.length === 0}
          className="px-6 py-2 text-sm font-bold text-white bg-blue-600 rounded-md  min-w-[140px]"
        >
          Create RFQ
        </button>
      </div>
    </div>
  </div>
</div>
    </div>

{showAddAddressModal && (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-2xl w-full max-w-md max-h-[85vh] overflow-hidden shadow-2xl flex flex-col">
      
      {/* Header - YOUR ORIGINAL */}
      <div className="flex justify-between items-center p-4 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <h3 className="text-md font-semibold ">Add New Address</h3>
        </div>
        <button onClick={() => setShowAddAddressModal(false)}>
          <FontAwesomeIcon icon={faX} className="w-3 h-3" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        
        {/* YOUR TABS - SAME */}
        <div className="flex bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-1 mb-4 shadow-md">
          {['project', 'other'].map((tab) => (
            <button
              key={tab}
              className={`flex-1 py-2 px-4 text-xs font-semibold rounded-lg transition-all ${
                activeTab === tab
                  ? 'bg-blue-500 shadow-sm text-white border border-blue-200'
                  : 'text-gray-600 hover:bg-white'
              }`}
              onClick={() => setActiveTab(tab)}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Form Fields - YOUR ORIGINAL */}
        <div className="space-y-4">
          {activeTab === 'project' && (
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Project Name <span className="text-red-500">*</span>
              </label>
              <input
                value={formData.projectName || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, projectName: e.target.value }))}
                className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 text-sm"
                placeholder="Enter project name"
              />
            </div>
          )}

          {/* WORKING MAP SECTION - NEW */}
<div>
  <label className="block text-xs font-semibold text-gray-700 mb-1">
    Area / Locality / Sector <span className="text-red-500">*</span>
  </label>
  
  <PlacesAutocomplete 
    value={address} 
    onChange={setAddress} 
    onSelect={handleAddressSelect}
    searchOptions={{ 
      types: ['geocode'], 
      componentRestrictions: { country: 'in' } 
    }}
  >
    {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
      <div className="relative">
        {/* FIXED: value prop use chey, getInputProps lo value ivvaku */}
        <input
          {...getInputProps({
            placeholder: 'Search area, apartment or pincode...',
            className: 'w-full p-3 pr-10 border border-gray-200 rounded-lg focus:ring-2 focus:border-blue-500 text-sm'
          })}
        />
        
        <FontAwesomeIcon icon={faSearch} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
        
        {/* Loading */}
        {loading && (
          <div className="absolute mt-10 bg-white border border-gray-200 rounded-lg w-full shadow-lg p-3 z-20">
            <div className="flex items-center text-sm text-gray-600">
              <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mr-2"></div>
              Searching...
            </div>
          </div>
        )}
        
        {/* Suggestions */}
        {suggestions.length > 0 && (
          <div className="absolute mt-1 bg-white border border-gray-200 rounded-lg w-full shadow-lg z-[9999] max-h-48 overflow-y-auto">
            {suggestions.map(suggestion => (
              <div 
                key={suggestion.placeId}
                {...getSuggestionItemProps(suggestion, {
                  className: suggestion.active 
                    ? 'px-4 py-2.5 bg-blue-500 text-white cursor-pointer flex items-center' 
                    : 'px-4 py-2.5 hover:bg-gray-50 cursor-pointer border-b border-gray-50 last:border-b-0 flex items-center'
                })}
              >
                <FontAwesomeIcon icon={faMapMarkerAlt} className={`w-4 h-4 mr-3 ${suggestion.active ? 'text-white' : 'text-gray-400'}`} />
                <span className="text-sm">{suggestion.description}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    )}
  </PlacesAutocomplete>
</div>

          {/* Landmark - YOUR ORIGINAL */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-4">
              Nearby Landmark
            </label>
            <input
              value={formData.nearbyLandmark || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, nearbyLandmark: e.target.value }))}
              className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 text-sm"
              placeholder=" "
            />
          </div>
        </div>
      </div>

      {/* Footer - YOUR ORIGINAL */}
      <div className="p-4 border-t bg-gradient-to-r from-gray-50 to-white">
        <div className="flex gap-2 justify-end">
          <button
            onClick={() => setShowAddAddressModal(false)}
            className="px-6 py-2 text-xs font-semibold text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSaveAddress}
            disabled={!formData.areaLocalitySector}
            className="px-6 py-2 text-xs font-bold text-white bg-blue-600 rounded-lg shadow-md"
          >
            Save Address
          </button>
        </div>
      </div>
    </div>
  </div>
)}


{showPopup && (
  <div className="z-[9999]">
    {popupType === '200or201' && <Msg200or201 message={popupMessage} timeout={3000} />}
    {popupType === '204' && <Msg204 message={popupMessage} timeout={3000} />}
    {popupType === '400' && <Msg400 message={popupMessage} closePopup={closePopup} />}
    {popupType === '409' && <Msg409 message={popupMessage} closePopup={closePopup} />}
        
  </div>
)}
  </div>
);

};


const QuantitySelector = ({ item, quantity, handleQuantityChange, min = 1, max = Infinity, index }) => {
  const [inputValue, setInputValue] = useState(quantity.toString());

  // Sync inputValue with quantity when quantity changes (e.g., from buttons)
  useEffect(() => {
    setInputValue(quantity.toString());
  }, [quantity]);

  // Handle input change (allow any typing, including clearing)
  const handleInputChange = (e) => {
    setInputValue(e.target.value); // Update local state freely
  };

  // Handle blur (validate and update parent)
  const handleInputBlur = () => {
    const numValue = parseInt(inputValue, 10);
    let finalValue;
    if (isNaN(numValue) || numValue < min) {
      finalValue = min; // Clamp to min if invalid or too low
    } else if (numValue > max) {
      finalValue = max; // Clamp to max if too high
    } else {
      finalValue = numValue; // Valid number
    }
    handleQuantityChange(item.id, finalValue, index); // Update parent
    setInputValue(finalValue.toString()); // Sync local state
  };

  return (
    <div className="flex items-center gap-2">
      <button
        onClick={() => handleQuantityChange(item.id, "dec", index)}
        className="w-7 h-7 flex items-center justify-center border border-gray-300 rounded-full hover:bg-gray-100"
       // disabled={quantity <= min}
      >
        <Minus size={14} />
      </button>
      <input
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        className="w-12 text-center text-sm border border-gray-300 rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
        min={min}
        max={max}
      />
      <button
        onClick={() => handleQuantityChange(item.id, "inc", index)}
        className="w-7 h-7 flex items-center justify-center border border-gray-300 rounded-full hover:bg-gray-100"
        disabled={quantity >= max}
      >
        <Plus size={14} />
      </button>
    </div>
  );
};




export default CreateRFQ;



